import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@comps/ui/card";
import { ChartConfig, ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from "@comps/ui/chart";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { ChartDataItem, getWidgetStyle, LineChartData, WidgetProps } from "@dto/Dashboard";
import { AggregationType } from "@dto/fields";
import { aggregationToPeriodType, Period, PeriodType } from "@dto/period";
import { PathsResponse } from "@dto/records";
import { Group, TOP } from "@dto/views";
import { ChartColorType, getColor } from "@utils/colors";
import { getPaths } from "@utils/data";
import { pathKeyToPath } from "@utils/model";
import { toastError } from "@utils/toasts";
import { compareValues, isArray, valueToString } from "@utils/types";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CartesianGrid, LabelList, Line, LineChart, XAxis, YAxis } from "recharts";
import { Formatter, NameType, ValueType } from "recharts/types/component/DefaultTooltipContent";
import { TFunction } from "i18next";
import { translate } from "src/i18n/i18n";
import { useModelStore } from "src/store";
import { hideEmptyCategories } from "./chartCategories";

function isSubYearAggregation(agg: AggregationType): boolean {
    return [AggregationType.DAY, AggregationType.WEEK, AggregationType.MONTH, AggregationType.QUARTER].includes(agg);
}

function getSubYearLabel(period: Period, agg: AggregationType, t: TFunction): string {
    switch (agg) {
        case AggregationType.MONTH: return period.toString(t, "MMM");
        case AggregationType.QUARTER: return period.toString(t, "QQQ");
        case AggregationType.WEEK: return period.toString(t, "WWW");
        default: return period.toString(t, "dd/MM");
    }
}

function getSubYearSortKey(period: Period, agg: AggregationType): number {
    switch (agg) {
        case AggregationType.MONTH: return period.month();
        case AggregationType.QUARTER: return period.quarter();
        case AggregationType.WEEK: return period.week();
        default: return period.day();
    }
}

export function LineChartCard(props: WidgetProps) {
    const widget = props.widget.data as LineChartData;

    const objectInfos = useModelStore(state => state.objectInfos);
    const workspace = useModelStore(state => state.selectedWorkspace);
    const relationByKeys = useModelStore(state => state.relationByKeys);
    const fieldByKeys = useModelStore(state => state.fieldByKeys);
    const [chartData, setChartData] = useState<ChartDataItem[]>([]);
    const [chartConfig, setChartConfig] = useState<ChartConfig>({});
    const [categories, setCategories] = useState<string[]>([]);
    const pointsCoordinatesRef = useRef<Map<string, Array<{ x: number; y: number; index: number }>>>(new Map());
    const [pointsCoordinates, setPointsCoordinates] = useState<Map<string, Array<{ x: number; y: number; index: number }>>>(new Map());
    const { t } = useTranslation();
    const valuePathKey = widget.valuePath;
    const valueAggregation = widget.valueAggregation ?? AggregationType.NO_AGGR;
    const splitByPathKey = widget.splitByPath;
    const splitByAggregation = widget.splitByAggregation ?? AggregationType.NO_AGGR;
    const splitBySortType = widget.splitBySortType;
    const categoryPathKey = widget.categoryPath;
    const categoryAggregation = widget.categoryAggregation ?? AggregationType.NO_AGGR;
    const categorySortType = widget.categorySortType;
    const lineStyle = widget.lineStyle;
    const showDots = widget.showDots ?? false;
    const nullsAsZeros = widget.nullsAsZeros ?? false;
    const categoryByYear = widget.categoryByYear ?? false;
    const showEmptyCategories = widget.showEmptyCategories ?? false;
    const filter = widget.filter;

    const valuePath = pathKeyToPath(valuePathKey, relationByKeys);
    const valueField = fieldByKeys?.get(valuePath?.field ?? "");
    const splitByPath = pathKeyToPath(splitByPathKey, relationByKeys);
    const splitByField = fieldByKeys?.get(splitByPath?.field ?? "");
    const categoryPath = pathKeyToPath(categoryPathKey, relationByKeys);
    const categoryField = fieldByKeys?.get(categoryPath?.field ?? "");
    const aggregationMap = new Map<string, AggregationType>();
    if (valuePathKey && valueAggregation) {
        aggregationMap.set(valuePathKey, valueAggregation);
    }
    if (splitByPathKey && splitByAggregation) {
        aggregationMap.set(splitByPathKey, splitByAggregation);
    }
    if (categoryPathKey && categoryAggregation) {
        aggregationMap.set(categoryPathKey, categoryAggregation);
    }

    const defaultValue: number | null = (nullsAsZeros) ? 0 : null;
    const categoryFormat = categoryField?.format;
    
    useEffect(() => {
        if (!workspace) {
            return;
        }
        const srcObjectKey = valuePath?.srcObject ?? valuePath?.object;
        if (!valuePath || !splitByPath || !srcObjectKey) {
            return;
        }

        if (!categoryPath) {
            const sortings = [{ pathKey: splitByPathKey!, sort: splitBySortType }];
            const groups: Group[] = [{ pathKey: splitByPathKey!, sort: splitBySortType, hideEmptyGroups: false, displayType: TOP, applyGroupFilter: false, filter: null }];
            getPaths(workspace._key, srcObjectKey, [valuePath, splitByPath], null, filter, sortings, groups, null, null, aggregationMap).then(response => {
                if (response) {
                    if (isCompozzError(response)) {
                        const cError = response as CompozzError;
                        if (cError.i18n) {
                            toastError(translate(t, cError.i18n, cError.params));
                        } else {
                            toastError(cError.message);
                        }
                    } else {
                        const pathsResponse = response as PathsResponse;
                        if (pathsResponse.lines && pathsResponse.lines.length > 0) {
                            const chartData: any[] = []
                            const chartConfig: ChartConfig = {}
                            const categories: string[] = []
                            const valuesBySplitBy: Map<string, Map<string, number>> = new Map();

                            if (categoryByYear && isSubYearAggregation(splitByAggregation)) {
                                const sortKeyBySplitBy: Map<string, number> = new Map();
                                const yearsSet: Set<string> = new Set();

                                for (const group of pathsResponse.lines) {
                                    if (group.children) {
                                        for (const line of group.children) {
                                            let rawSplitBy = line.items[1].value ?? "";
                                            if (isArray(rawSplitBy)) rawSplitBy = rawSplitBy[0];
                                            const period = Period.fromObject(rawSplitBy);
                                            if (!period) {
                                                continue;
                                            }
                                            const yearLabel = period.year().toString();
                                            const subKey = getSubYearLabel(period, splitByAggregation, t);
                                            const sortKey = getSubYearSortKey(period, splitByAggregation);
                                            const value = line.items[0].value ?? null;
                                            const values = valuesBySplitBy.get(subKey) ?? new Map<string, number>();
                                            values.set(yearLabel, value);
                                            valuesBySplitBy.set(subKey, values);
                                            sortKeyBySplitBy.set(subKey, sortKey);
                                            yearsSet.add(yearLabel);
                                        }
                                    }
                                }

                                const sortedYears = Array.from(yearsSet).sort();
                                sortedYears.forEach((year, idx) => {
                                    chartConfig[year] = { label: year, color: getColor(ChartColorType, idx) };
                                    categories.push(year);
                                });

                                valuesBySplitBy.forEach((values, subKey) => {
                                    const chartDataItem: any = { splitBy: subKey, _sortKey: sortKeyBySplitBy.get(subKey) ?? 0 };
                                    sortedYears.forEach(year => {
                                        chartDataItem[year] = values.get(year) ?? defaultValue;
                                    });
                                    chartData.push(chartDataItem);
                                });

                                chartData.sort((a, b) => a._sortKey - b._sortKey);
                            } else {
                                const category = "Category";
                                for (const group of pathsResponse.lines) {
                                    if (group.children) {
                                        for (const line of group.children) {
                                            let splitBy = line.items[1].value ?? "";
                                            if (isArray(splitBy)) {
                                                splitBy = splitBy[0];
                                            }
                                            let splitByKey = valueToString(splitBy, splitByField!, splitByAggregation, undefined, t, "", fieldByKeys);
                                            if (!splitByKey) {
                                                splitByKey = t("dashboard.label.empty");
                                            }
                                            const value = line.items[0].value ?? null;
                                            const values = valuesBySplitBy.get(splitByKey) ?? new Map<string, number>();
                                            values.set(category, value);
                                            valuesBySplitBy.set(splitByKey, values);
                                        }
                                    }
                                }
                                const barColor = getColor(ChartColorType, 0);
                                chartConfig[category] = { label: category, color: barColor };
                                categories.push(category);

                                valuesBySplitBy.forEach((values, splitByKey) => {
                                    const chartDataItem: any = { splitBy: splitByKey };
                                    values.forEach((value, category) => { chartDataItem[category] = value; });
                                    categories.forEach((category) => {
                                        if (chartDataItem[category] == undefined || chartDataItem[category] == null) {
                                            chartDataItem[category] = defaultValue;
                                        }
                                    });
                                    chartData.push(chartDataItem);
                                });

                                chartData.sort((a, b) => {
                                    return compareValues(a.splitBy, b.splitBy, splitByField!, splitByAggregation, splitBySortType, t, fieldByKeys);
                                });
                            }

                            setChartData(chartData);
                            setChartConfig(chartConfig);
                            setCategories(categories);
                        } else {
                            setChartData([]);
                            setChartConfig({});
                            setCategories([]);
                        }
                    }
                }
            })
        } else {
            const sortings = [{ pathKey: splitByPathKey!, sort: splitBySortType }];
            const hideEmptyGroups = hideEmptyCategories(showEmptyCategories, filter, categoryPathKey!);
            const groups: Group[] = [{ pathKey: categoryPathKey!, sort: categorySortType, hideEmptyGroups: hideEmptyGroups, displayType: TOP, applyGroupFilter: false, filter: null }];
            getPaths(workspace._key, srcObjectKey, [valuePath, splitByPath, categoryPath], null, filter, sortings, groups, null, null, aggregationMap).then(response => {
                if (response) {
                    if (isCompozzError(response)) {
                        const cError = response as CompozzError;
                        if (cError.i18n) {
                            toastError(translate(t, cError.i18n, cError.params));
                        } else {
                            toastError(cError.message);
                        }
                    } else {
                        const pathsResponse = response as PathsResponse;
                        if (pathsResponse.lines && pathsResponse.lines.length > 0) {
                            const chartData: any[] = []
                            const chartConfig: ChartConfig = {}
                            const categories: string[] = []
                            let index = 0;
                            const valuesBySplitBy: Map<string, Map<string, number>> = new Map();
                            for (const group of pathsResponse.lines) {
                                let category = group.items[0].value ?? "";
                                if (isArray(category)) {
                                    category = valueToString(category[0], categoryField!, categoryAggregation, categoryFormat, t, "", fieldByKeys);
                                } else {
                                    category = valueToString(category, categoryField!, categoryAggregation, categoryFormat, t, "", fieldByKeys);
                                }
                                if (!category) {
                                    category = t("dashboard.label.empty");
                                }
                                const barColor = getColor(ChartColorType, index);
                                if (group.children) {
                                    for (const line of group.children) {
                                        let splitBy = line.items[1].value ?? "";
                                        if (isArray(splitBy)) {
                                            splitBy = splitBy[0];
                                        }
                                        let splitByKey = valueToString(splitBy, splitByField!, splitByAggregation, undefined, t, "", fieldByKeys);
                                        if (!splitByKey) {
                                            splitByKey = t("dashboard.label.empty");
                                        }
                                        const value = line.items[0].value ?? null;
                                        const values = valuesBySplitBy.get(splitByKey) ?? new Map<string, number>();
                                        values.set(category, value);
                                        valuesBySplitBy.set(splitByKey, values);
                                    }
                                }
                                chartConfig[category] = {
                                    label: category,
                                    color: barColor,
                                }
                                categories.push(category);
                                index++;
                            }

                            valuesBySplitBy.forEach((values, splitByKey) => {
                                const chartDataItem: any = {
                                    splitBy: splitByKey,
                                }

                                values.forEach((value, category) => {
                                    chartDataItem[category] = value;
                                })
                                categories.forEach((category) => {
                                    if (chartDataItem[category] == undefined || chartDataItem[category] == null) {
                                        chartDataItem[category] = defaultValue;
                                    }
                                })
                                chartData.push(chartDataItem);
                            })

                            chartData.sort((a, b) => {
                                return compareValues(a.splitBy, b.splitBy, splitByField!, splitByAggregation, splitBySortType, t, fieldByKeys);
                            });

                            categories.sort((a, b) => {
                                return compareValues(a, b, categoryField!, categoryAggregation, categorySortType, t, fieldByKeys);
                            });

                            setChartData(chartData);
                            setChartConfig(chartConfig);
                            setCategories(categories);
                        } else {
                            // setValues(new Map());
                            setChartData([]);
                            setChartConfig({});
                            setCategories([]);
                        }
                    }
                }
            })
        }
    }, [objectInfos, fieldByKeys, widget, filter, t]);

    // Reset coordinates when data changes
    useEffect(() => {
        pointsCoordinatesRef.current.clear();
        setPointsCoordinates(new Map());
    }, [chartData, categories]);

    // Update state from ref after a short delay to allow coordinates to be collected
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setPointsCoordinates(new Map(pointsCoordinatesRef.current));
        }, 0);
        return () => clearTimeout(timeoutId);
    }, [chartData, categories]);

    const srcObjectKey = valuePath?.srcObject ?? valuePath?.object;
    if (!valuePathKey || !splitByPathKey || !srcObjectKey) {
        return <div>{t("dashboard.overview.no.data")}</div>;
    }

    if (!workspace) {
        return <div>{t("dashboard.overview.not.configured")}</div>;
    }

    if (chartData.length === 0) {
        return <div>{t("dashboard.overview.no.data")}</div>;
    }

    let xAxisDataKey = "splitBy";
    let xAxisIsPeriod = false;
    // if (splitByField && fieldToOutputType(splitByField, splitByAggregation) === FieldType.Period) {
    //     xAxisDataKey = "splitBy.date";
    //     xAxisIsPeriod = true;
    // }
    const style = getWidgetStyle(props.widget);

    let formatter: Formatter<ValueType, NameType> | undefined = undefined;
    const valueFormat = valueField?.format;
    const valueFormatter = (value: ValueType) => valueToString(value, valueField!, valueAggregation, valueFormat, t, "", fieldByKeys);
    if (!categoryPath) {
        formatter = (value: any) => {
            if (value) {
                return "valeur : " + valueFormatter(value);
            }
            return "";
        }
    }
    return (
        <Card>
            {props.widget.title &&
                <CardHeader className="items-center">
                    <CardTitle>{props.widget.title}</CardTitle>
                    {props.widget.description &&
                        <CardDescription>
                            {props.widget.description}
                        </CardDescription>
                    }
                </CardHeader>
            }

            <CardContent className="pb-0">

                <ChartContainer
                    config={chartConfig}
                    className="mx-auto aspect-square" style={style}>
                    <LineChart
                        accessibilityLayer
                        data={chartData}
                        margin={{
                            left: 40,
                            right: 40,
                            top: 12,
                            bottom: 12,
                        }}
                    >
                        <CartesianGrid vertical={false} />
                        <YAxis 
                            tickLine={false} 
                            tickMargin={20} 
                            axisLine={true} 
                            padding={{ top: 20, bottom: 0 }}
                            hide={props.widget.hideYAxis === true}/>
                        <XAxis
                            dataKey={xAxisDataKey}
                            type="category"
                            tickLine={false}
                            tickMargin={10}
                            axisLine={true}
                            tickFormatter={(value: any) => {
                                if (!value) {
                                    return "";
                                }
                                if (xAxisIsPeriod) {
                                    const periodType = aggregationToPeriodType(splitByAggregation);
                                    const period = Period.create(value, periodType);
                                    return period.toString(t);
                                }
                                return value;
                            }}
                            angle={0}
                        />
                        <ChartTooltip
                            cursor={false}
                            content={<ChartTooltipContent hideLabel hideIndicator={categoryPath ? false : true} formatter={formatter} valueFormatter={valueFormatter} />}
                        />

                        {categories.map((category) => (
                            <Line
                                key={category}
                                dataKey={category}
                                type={lineStyle}
                                stroke={chartConfig[category]?.color ?? ""}
                                strokeWidth={2}
                                dot={showDots}
                                connectNulls
                            >
                                <LabelList
                                    content={(labelProps: any) => {
                                        // Store coordinates of each point in the ref (needed for percentages)
                                        if (labelProps.x !== undefined && labelProps.y !== undefined && labelProps.index !== undefined) {
                                            const coords = pointsCoordinatesRef.current.get(category) || [];
                                            const newCoords = [...coords];
                                            newCoords[labelProps.index] = { x: labelProps.x, y: labelProps.y, index: labelProps.index };
                                            pointsCoordinatesRef.current.set(category, newCoords);
                                        }
                                        
                                        // Display value on point only if showValues is enabled
                                        if (props.widget.showValues !== true) {
                                            return null;
                                        }
                                        
                                        const value = labelProps.value;
                                        if (value === null || value === undefined) {
                                            return null;
                                        }
                                        
                                        const valueStr = valueToString(value, valueField!, valueAggregation, valueFormat, t, "", fieldByKeys);
                                        
                                        // Estimate text width (approximate: ~6px per character)
                                        const textWidth = valueStr.length * 6;
                                        const padding = 4;
                                        const rectWidth = textWidth + padding * 2;
                                        const rectHeight = 16;
                                        const rectX = (labelProps.x ?? 0) - rectWidth / 2;
                                        const rectY = (labelProps.y ?? 0) - 12 - rectHeight / 2;
                                        
                                        return (
                                            <g>
                                                <rect
                                                    x={rectX}
                                                    y={rectY}
                                                    width={rectWidth}
                                                    height={rectHeight}
                                                    fill="white"
                                                    fillOpacity={0.9}
                                                    stroke="none"
                                                    rx={2}
                                                />
                                                <text
                                                    x={labelProps.x}
                                                    y={(labelProps.y ?? 0) - 12}
                                                    fill="#333"
                                                    fontSize={11}
                                                    textAnchor="middle"
                                                >
                                                    {valueStr}
                                                </text>
                                            </g>
                                        );
                                    }}
                                />
                            </Line>
                        ))}
                        {/* Custom component to draw labels in the middle of segments */}
                        {props.widget.showPercentage === true && (
                            <g>
                                {categories.map((category) => {
                                    const coordinates = pointsCoordinates.get(category) || [];
                                    if (coordinates.length < 2) return null;
                                    
                                    return coordinates.map((point, idx) => {
                                        if (idx === 0) return null;
                                        const prevPoint = coordinates[idx - 1];
                                        if (!prevPoint || !point || !prevPoint.x || !prevPoint.y || !point.x || !point.y) return null;
                                        
                                        const currentValue = (chartData[idx] as any)?.[category];
                                        const previousValue = (chartData[idx - 1] as any)?.[category];
                                        
                                        if (currentValue === null || currentValue === undefined || 
                                            previousValue === null || previousValue === undefined || previousValue === 0) {
                                            return null;
                                        }
                                        
                                        const percentage = ((currentValue - previousValue) / Math.abs(previousValue)) * 100;
                                        const sign = percentage >= 0 ? '+' : '';
                                        const percentageStr = `${sign}${percentage.toFixed(1)}%`;
                                        
                                        const midX = (prevPoint.x + point.x) / 2;
                                        const midY = (prevPoint.y + point.y) / 2;
                                        
                                        // Estimate text width (approximate: ~6px per character)
                                        const textWidth = percentageStr.length * 6;
                                        const padding = 4;
                                        const rectWidth = textWidth + padding * 2;
                                        const rectHeight = 16;
                                        const rectX = midX - rectWidth / 2;
                                        const rectY = midY - 12 - rectHeight / 2;
                                        
                                        return (
                                            <g key={`mid-${category}-${idx}`}>
                                                <rect
                                                    x={rectX}
                                                    y={rectY}
                                                    width={rectWidth}
                                                    height={rectHeight}
                                                    fill="white"
                                                    fillOpacity={0.9}
                                                    stroke="none"
                                                    rx={2}
                                                />
                                                <text
                                                    x={midX}
                                                    y={midY - 8}
                                                    fill="#666"
                                                    fontSize={11}
                                                    textAnchor="middle"
                                                >
                                                    {percentageStr}
                                                </text>
                                            </g>
                                        );
                                    });
                                })}
                            </g>
                        )}
                        {props.widget.showLegend && (categoryPath || categoryByYear) && <ChartLegend content={<ChartLegendContent className="flex-wrap" />} />}
                    </LineChart>
                </ChartContainer>
            </CardContent>
        </Card>
    );
}